/**
 * <RevisionProgramCard> — a study-program / day-item row (organism, L0.1).
 *
 * Mirrors legacy `StudyItemComponent` layout: one row, two columns — subject icon (40px) on the
 * left, title (subject-colored) + "Subject • Nh" meta on the right. Pressable only when `onPress`
 * is set (parent detail is view-only).
 *
 * Built on the generic `<Card>`; composes DS atoms only — `<Stack>`, `<Row>`, `<ImageIcon>`,
 * `<Title>`, `<Text>`, `<Badge>`.
 *
 * A stage séance says its hour instead of a duration (« Mathématiques • 10h00 »), and its notes
 * follow as chips (`chips`, written by the adapter).
 */
import { StyleSheet } from "react-native-unistyles"

import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { ImageIcon } from "../../atoms/icons/ImageIcon"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../../molecules/Card"
import { type UIRevisionProgram } from "../../../ports/primary/ui/types"

export type RevisionProgramCardProps = {
    program: UIRevisionProgram
    onPress?: () => void
    testID?: string
}

export function RevisionProgramCard(props: RevisionProgramCardProps) {
    const { program, onPress, testID } = props
    const { title, subject, durationHours, time, chips } = program

    // Legacy StudyItemComponent: "{name}" or "{name} • {duration}h"; a stage séance says its hour.
    const meta =
        time != null
            ? `${subject.name} • ${time}`
            : durationHours != null
              ? `${subject.name} • ${durationHours}h`
              : subject.name

    return (
        <Card pressable={!!onPress} onPress={onPress} testID={testID}>
            <Row gap="3" align="center">
                <ImageIcon uri={subject.iconUrl} size={40} />
                <Stack gap="1" style={styles.body}>
                    <Title size="text-lg" color={subject.color} numberOfLines={1}>
                        {title}
                    </Title>
                    <Text size="text-sm" weight="regular" color="text-muted">
                        {meta}
                    </Text>
                    {chips != null && chips.length > 0 && (
                        <Row gap="2" style={styles.chips}>
                            {chips.map((chip) => (
                                <Badge key={chip.text} text={chip.text} variant={chip.tone} />
                            ))}
                        </Row>
                    )}
                </Stack>
            </Row>
        </Card>
    )
}

const styles = StyleSheet.create(() => ({
    body: {
        flex: 1,
        flexShrink: 1,
    },
    chips: {
        flexWrap: "wrap",
    },
}))
